import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CashierLiveBurns } from "@/components/CashierLiveBurns";
import { CashierTodayList } from "@/components/CashierTodayList";
import { CashierApprovalQueue } from "@/components/CashierApprovalQueue";
import { CashierManualRedeem } from "@/components/CashierManualRedeem";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { useKioskMode } from "@/hooks/use-kiosk-mode";
import { getStaffMerchantContext } from "@/lib/staff.functions";
import { withAuth } from "@/lib/server-fn-auth";
import { Sentry } from "@/lib/sentry";
import { Maximize2, Minimize2 } from "lucide-react";


export const Route = createFileRoute("/_cashier/cashier")({
  component: CashierPage,
});

function CashierPage() {
  const { t } = useTranslation();
  const { current, loading: locLoading } = useCurrentLocation();
  const merchantId = current?.id ?? null;
  const [businessName, setBusinessName] = useState<string>("");
  const [redemptionStyle, setRedemptionStyle] = useState<string>("quick_burn");
  const [loading, setLoading] = useState(true);
  const { kiosk, setKiosk } = useKioskMode();

  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    setLoading(true);
    // Server-side read: `merchants` SELECT is owner-only, so invited staff
    // must go through the is_merchant_staff-verified server function.
    withAuth(getStaffMerchantContext)({ data: { merchantId } })
      .then((res) => {
        if (cancelled) return;
        setBusinessName(res.businessName);
        setRedemptionStyle(res.redemptionStyle);
        Sentry.setTag("merchant_slug", res.slug);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [merchantId]);

  if (locLoading || loading || !merchantId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">{t("cashier.loading")}</div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-background">
      <div className={`${kiosk ? "max-w-[1400px]" : "max-w-3xl"} mx-auto px-4 py-6 space-y-5`}>
        <header className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-display font-bold">{businessName}</h1>
            <p className="text-sm text-muted-foreground">{t("cashier.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!kiosk && (
              <button
                onClick={() => setKiosk(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-border bg-card hover:bg-accent text-foreground transition-colors"
                title={t("cashier.kioskTitle")}
              >
                <Maximize2 className="size-4" />
                <span>{t("cashier.kioskMode")}</span>
              </button>
            )}

          </div>
        </header>

        <CashierManualRedeem redemptionStyle={redemptionStyle} merchantId={merchantId} />

        <CashierLiveBurns merchantId={merchantId} redemptionStyle={redemptionStyle} />

        {redemptionStyle === "verified_voucher" && (
          <CashierApprovalQueue merchantId={merchantId} />
        )}

        <CashierTodayList merchantId={merchantId} />
      </div>

      {kiosk && (
        <button
          onClick={() => setKiosk(false)}
          className="fixed bottom-4 right-4 z-50 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium bg-foreground/70 hover:bg-foreground text-background backdrop-blur-sm shadow-lg transition-colors"
          title={t("cashier.exitKiosk")}
        >
          <Minimize2 className="size-4" />
          <span>{t("cashier.exitKiosk")}</span>

        </button>
      )}
    </div>
  );
}
